import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMonitor, FiWifi, FiWifiOff, FiLock, FiUnlock, FiPower, FiRefreshCw, FiKey, FiCopy, FiCheck, FiClock } from 'react-icons/fi';
import { getDevices, blockDevice, unblockDevice, shutdownDevice, pingDevice } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import api from '../services/api';

const Devices = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [devices, setDevices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{id: string, action: string} | null>(null);
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  const [uninstallPassword, setUninstallPassword] = useState<{password: string, valid_until: string} | null>(null);
  const [copied, setCopied] = useState(false);
  const [pingingDevice, setPingingDevice] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterBlocked, setFilterBlocked] = useState<string>('all');
  const [filterGroup, setFilterGroup] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchDevices = async () => {
    try {
      const res = await getDevices();
      setDevices(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
    const interval = setInterval(fetchDevices, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleAction = async (id: string, action: string) => {
    setActionLoading(id + action);
    try {
      if (action === 'block') {
        await blockDevice(id);
        // Чекаємо 3 секунди щоб агент встиг надіслати recovery key
        await new Promise(r => setTimeout(r, 3000));
        try {
          const keyRes = await api.get(`/devices/${id}/recovery-key`);
          if (keyRes.data.recovery_key) {
            setRecoveryCode(keyRes.data.recovery_key);
          }
        } catch {}
      } else if (action === 'unblock') {
        await unblockDevice(id);
        try {
          const keyRes = await api.get(`/devices/${id}/recovery-key`);
          if (keyRes.data.recovery_key) {
            setRecoveryCode(keyRes.data.recovery_key);
          }
        } catch {}
      } else if (action === 'shutdown') {
        await shutdownDevice(id);
      }
      await fetchDevices();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };

  const handlePing = async (id: string) => {
    setPingingDevice(id);
    try {
      await pingDevice(id);
      await fetchDevices();
    } catch (err) {
      console.error(err);
    } finally {
      setPingingDevice(null);
    }
  };

  const handleGetUninstallPassword = async (id: string) => {
    try {
      const res = await api.get(`/devices/${id}/uninstall-password`);
      setUninstallPassword(res.data);
      setCopied(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const groups = Array.from(new Set(devices.map((d: any) => d.group_name).filter(Boolean))) as string[];

  const filteredDevices = devices.filter(d => {
    if (filterStatus !== 'all' && d.status !== filterStatus) return false;
    if (filterBlocked === 'blocked' && !d.is_blocked) return false;
    if (filterBlocked === 'unblocked' && d.is_blocked) return false;
    if (filterGroup !== 'all' && d.group_name !== filterGroup) return false;
    if (searchQuery && !d.hostname.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !d.serial_number?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-slate-400">Loading...</div></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-white text-3xl font-bold">{t('devices')} <span className="text-slate-400 text-xl">({filteredDevices.length}/{devices.length})</span></h1>
        <button onClick={fetchDevices}
          className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-colors">
          <FiRefreshCw />{t('refresh')}
        </button>
      </div>
      <div className="flex flex-wrap gap-3 mb-6">
        <input
          type="text"
          placeholder="Search by hostname or serial..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="bg-slate-700 text-white px-4 py-2 rounded-lg text-sm flex-1 min-w-48 outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="bg-slate-700 text-white px-4 py-2 rounded-lg text-sm outline-none">
          <option value="all">All Status</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
        </select>
        <select value={filterBlocked} onChange={e => setFilterBlocked(e.target.value)}
          className="bg-slate-700 text-white px-4 py-2 rounded-lg text-sm outline-none">
          <option value="all">All Devices</option>
          <option value="blocked">Blocked</option>
          <option value="unblocked">Not Blocked</option>
        </select>
        <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)}
          className="bg-slate-700 text-white px-4 py-2 rounded-lg text-sm outline-none">
          <option value="all">All Groups</option>
          {groups.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      {devices.length === 0 ? (
        <div className="bg-slate-800 rounded-2xl p-12 text-center">
          <FiMonitor className="text-slate-600 text-6xl mx-auto mb-4" />
          <p className="text-slate-400 text-lg">{t('noDevices')}</p>
          <p className="text-slate-500 text-sm mt-2">{t('noDevicesDesc')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDevices.map((device) => (
            <div key={device.id} className="bg-slate-800 rounded-2xl p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${device.is_blocked ? 'bg-red-600' : 'bg-blue-600'}`}>
                    <FiMonitor className="text-white text-xl" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">{device.hostname}</h3>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-slate-400 text-sm">{device.os_type}</span>
                      <span className="text-slate-600">•</span>
                      <span className="text-slate-400 text-sm">{device.group_name || t('noGroup')}</span>
                      {device.serial_number && <>
                        <span className="text-slate-600">•</span>
                        <span className="text-slate-500 text-xs font-mono">SN: {device.serial_number}</span>
                      </>}
                      {device.agent_version && <>
                        <span className="text-slate-600">•</span>
                        <span className="text-slate-500 text-xs">v{device.agent_version}</span>
                      </>}
                      <span className="text-slate-600">•</span>
                      <span className="text-slate-400 text-sm">
                        {device.last_seen
                          ? `${t('lastSeen')}: ${new Date(device.last_seen).toLocaleString()}`
                          : t('neverConnected')}
                      </span>
                      {device.last_blocked_at && <>
                        <span className="text-slate-600">•</span>
                        <span className="text-red-400 text-xs">🔒 Blocked: {new Date(device.last_blocked_at).toLocaleString()}</span>
                      </>}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    device.status === 'online' ? 'bg-green-500/20 text-green-400' : 'bg-slate-600 text-slate-400'
                  }`}>
                    {device.status === 'online'
                      ? <span className="flex items-center gap-1"><FiWifi /> Online</span>
                      : <span className="flex items-center gap-1"><FiWifiOff /> Offline</span>}
                  </span>

                  <button
                    onClick={() => handlePing(device.id)}
                    disabled={pingingDevice === device.id}
                    title="Force refresh status"
                    className="flex items-center gap-1 bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-white px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <FiRefreshCw className={pingingDevice === device.id ? 'animate-spin' : ''} />
                  </button>

                  <button
                    onClick={() => navigate(`/devices/${device.id}/history`)}
                    title="View history"
                    className="flex items-center gap-1 bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-white px-3 py-2 rounded-lg transition-colors"
                  >
                    <FiClock />
                  </button>

                  {device.is_blocked ? (
                    <button onClick={() => setConfirmAction({id: device.id, action: 'unblock'})}
                      disabled={!!actionLoading}
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
                      <FiUnlock />{t('unblock')}
                    </button>
                  ) : (
                    <button onClick={() => setConfirmAction({id: device.id, action: 'block'})}
                      disabled={!!actionLoading}
                      className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
                      <FiLock />{t('block')}
                    </button>
                  )}

                  <button onClick={() => setConfirmAction({id: device.id, action: 'shutdown'})}
                    disabled={!!actionLoading}
                    className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
                    <FiPower />{t('shutdown')}
                  </button>


                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-white text-xl font-semibold mb-4">
              {confirmAction.action === 'block' && t('blockDevice')}
              {confirmAction.action === 'unblock' && t('unblockDevice')}
              {confirmAction.action === 'shutdown' && t('shutdownDevice')}
            </h3>
            <p className="text-slate-400 mb-6">
              {confirmAction.action === 'block' && t('blockDeviceDesc')}
              {confirmAction.action === 'unblock' && t('unblockDeviceDesc')}
              {confirmAction.action === 'shutdown' && t('shutdownDeviceDesc')}
            </p>
            <div className="flex gap-3">
              <button onClick={() => handleAction(confirmAction.id, confirmAction.action)}
                disabled={!!actionLoading}
                className={`flex-1 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 ${
                  confirmAction.action === 'unblock'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}>
                {actionLoading ? t('executing') : t('confirm')}
              </button>
              <button onClick={() => setConfirmAction(null)}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-semibold transition-colors">
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {recoveryCode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-white text-xl font-semibold mb-2">🔐 BitLocker Recovery Key</h3>
            <p className="text-slate-400 mb-4 text-sm">Збережіть цей ключ. Він знадобиться для розблокування пристрою.</p>
            <div className="bg-slate-900 border border-green-500/30 rounded-xl p-4 text-center mb-4">
              <p className="text-green-400 font-mono text-base font-bold tracking-wider break-all">{recoveryCode}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => handleCopy(recoveryCode)}
                className="flex-1 flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-semibold">
                {copied ? <FiCheck /> : <FiCopy />}
                {copied ? t('copied') : t('copy')}
              </button>
              <button onClick={() => setRecoveryCode(null)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold">
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {uninstallPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-white text-xl font-semibold mb-4">🔑 {t('uninstallPasswordTitle')}</h3>
            <p className="text-slate-400 mb-4">{t('uninstallPasswordDesc')}</p>
            <div className="bg-slate-700 rounded-xl p-4 text-center mb-4">
              <p className="text-yellow-400 font-mono text-2xl font-bold tracking-widest">{uninstallPassword.password}</p>
            </div>
            <p className="text-slate-500 text-sm text-center mb-6">
              {t('validUntil')}: {new Date(uninstallPassword.valid_until).toLocaleString()}
            </p>
            <div className="flex gap-3">
              <button onClick={() => handleCopy(uninstallPassword.password)}
                className="flex-1 flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-semibold">
                {copied ? <FiCheck /> : <FiCopy />}
                {copied ? t('copied') : t('copy')}
              </button>
              <button onClick={() => setUninstallPassword(null)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold">
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Devices;
